import React, { useState, useEffect, SetStateAction, Dispatch } from 'react';
import { useTheme } from '@zydalabs/storefront-components';
import { Typography } from '@zydalabs/zac-react';
import Counter, { CounterVariantType } from 'modules/productsModule/components/Counter';
import { COUNTER_TYPE } from 'common/constants/counterTypes';
import { localizeCurrency } from 'common/utils';
import useTranslation from 'next-translate/useTranslation';
import { Option } from '../../../../../../service/types/generated';
import { StyledMultiAddOnWrapper, StyledCounterWrapper, StyledContentWrapper } from './style';
import { useIsMobile, usePersistedCountryDetails } from '../../../../../../common/hooks';

type OptionMutationType = {
  optionId: string;
  optionGroupId: string;
  optionQuantity?: number;
  stepBy?: number;
};

type MultiAddonType = {
  // TODO remove this from here after using UseLocalization in counter component
  isArabic: boolean;
  existedOptionId: string;
  existedOptionMinQuantity: number;
  disableIncrement?: boolean;
  option: Option;
  totalMultiAddonCount: number;
  initialCount?: number;
  setTotalMultiAddonCount: (totalMultiAddonCount: number) => void;
  addMultiAddOnOption: (option: OptionMutationType) => void;
  setError: (error: boolean) => void;
  setBulkCount?: Dispatch<SetStateAction<number>>;
  stepBy?: number;
  maximumBulkQuantity?: number;
};

const MultiAddon: React.FC<MultiAddonType> = ({
  isArabic,
  existedOptionId,
  existedOptionMinQuantity,
  disableIncrement,
  option,
  totalMultiAddonCount,
  initialCount = 0,
  setTotalMultiAddonCount,
  addMultiAddOnOption,
  setError,
  setBulkCount,
  stepBy = 1,
  maximumBulkQuantity,
}) => {
  const theme: any = useTheme();
  const isMobile = useIsMobile();
  const { lang } = useTranslation();
  const [count, setCount] = useState(initialCount);
  const [persistedCountryDetails] = usePersistedCountryDetails();

  const { currency } = persistedCountryDetails || {};

  useEffect(() => {
    setError(totalMultiAddonCount < existedOptionMinQuantity);
  }, [totalMultiAddonCount]);

  useEffect(() => {
    if (initialCount !== count) setCount(initialCount);
  }, [initialCount]);

  const onCountChange = value => {
    addMultiAddOnOption({
      optionId: option.uuid,
      optionGroupId: existedOptionId,
      optionQuantity: value,
    });

    setCount(value);
  };

  const onDecrement = () => {
    if (maximumBulkQuantity) {
      setBulkCount(prevS => prevS - stepBy);
    }
    setTotalMultiAddonCount(totalMultiAddonCount - stepBy);
  };

  const onIncrement = () => {
    if (maximumBulkQuantity) {
      setBulkCount(prevS => prevS + stepBy);
    }
    setTotalMultiAddonCount(totalMultiAddonCount + stepBy);
  };

  return (
    <StyledMultiAddOnWrapper>
      <Counter
        count={count}
        onChange={onCountChange}
        disableIncrement={disableIncrement}
        variant={COUNTER_TYPE.MULTIADDON as CounterVariantType}
        onDecrement={onDecrement}
        onIncrement={onIncrement}
        stepBy={stepBy}
      />
      <StyledContentWrapper
        onClick={() => {
          if (!disableIncrement) {
            setTotalMultiAddonCount(totalMultiAddonCount + 1);
            onIncrement();
            setCount(prevState => {
              addMultiAddOnOption({
                optionId: option.uuid,
                optionGroupId: existedOptionId,
                optionQuantity: prevState + stepBy,
              });
              return prevState + 1;
            });
          }
        }}
        data-testid="multiAddon-component"
        disableIncrement={disableIncrement}
      >
        <StyledCounterWrapper>
          <Typography
            variant={isMobile ? 'body16' : 'body14'}
            color={theme.color.headlineText}
            mx={12}
            testId="current-product-count"
            cursor={disableIncrement ? 'default' : 'pointer'}
          >
            {isArabic ? option.titleAr : option.titleEn}
          </Typography>
        </StyledCounterWrapper>
        {!!option.price && currency && (
          <Typography
            variant={isMobile ? 'body16' : 'body14'}
            color={theme.color.headlineText}
            cursor={disableIncrement ? 'default' : 'pointer'}
          >
            {localizeCurrency(lang, { amount: option.price, currency: currency.title_en })}
          </Typography>
        )}
      </StyledContentWrapper>
    </StyledMultiAddOnWrapper>
  );
};

export default MultiAddon;
